import { tokens } from "../styles/tokens";
import type { DivergenceRow } from "../api/rest";
import type { OccupancyCell } from "./OccupancyLayer";
import { boxAddress, type BoxPlacement } from "./palletLoad";
import type { PaintCounts } from "./expiryPaint";

export { boxAddress };

/**
 * "Pintar por WMS" (ADR 375): paint each bin by what the WMS comparison says
 * about it — the report's divergence classes (ADR 119), on the floor.
 *
 * Colors come from the SAME urgency ramp the Validade and Idade modes use
 * (`tokens.expiry`), so the three paint modes share one vocabulary instead of
 * inventing a palette each: red is always "act here", green is always "fine",
 * and nothing new competes with the status mode's red for unidentified stock
 * (ADR 154) — that red lives in a different mode and never on screen at the
 * same time.
 *
 *   qty_mismatch      red     both sides present, the numbers disagree
 *   missing_in_ic3    orange  the WMS books it here, the count did not find it
 *   unexpected_in_ic3 yellow  the count found it here, the WMS books nothing
 *   match             green   they agree
 *
 * Cells the comparison never reached — never visited (ADR 374), or no WMS
 * snapshot at all — carry no claim and are muted, never painted "fine". So are
 * the ADR 384 window classes (received / shipped during the count): the closing
 * snapshot explained them, and a bin that is only "explained" is not "fine".
 */

export type WmsClassKey = "qty_mismatch" | "missing_in_ic3" | "unexpected_in_ic3" | "match";

/** Legend order, hottest first, with each class's index into `tokens.expiry`. */
export const WMS_CLASSES = [
  { key: "qty_mismatch", ramp: 0 },
  { key: "missing_in_ic3", ramp: 1 },
  { key: "unexpected_in_ic3", ramp: 2 },
  { key: "match", ramp: 4 },
] as const;

/** Grey says "not compared", never "fine" — same rule as the Validade no-date grey. */
export const WMS_NO_COMPARISON_COLOR = tokens.ink[500];

const RAMP: Record<WmsClassKey, number> = {
  qty_mismatch: 0, missing_in_ic3: 1, unexpected_in_ic3: 2, match: 4,
};

/** One address can hold several (address, item) pairs in different classes.
 *  The hottest color wins, so a bin with any divergence never reads as "fine". */
const HEAT: WmsClassKey[] = ["qty_mismatch", "missing_in_ic3", "unexpected_in_ic3", "match"];

export function wmsColorFor(cls: WmsClassKey | null | undefined): string {
  return cls ? tokens.expiry[RAMP[cls]] : WMS_NO_COMPARISON_COLOR;
}

/**
 * Collapse a run's divergence rows to one class per address (hottest wins).
 * Rows whose status is not one of the four are ignored rather than trusted —
 * the wire type may grow a class this build does not know how to paint.
 */
export function wmsClassByAddress(rows: readonly DivergenceRow[]): Map<string, WmsClassKey> {
  const byAddress = new Map<string, WmsClassKey>();
  for (const r of rows) {
    const cls = r.status as WmsClassKey;
    if (!(cls in RAMP)) continue;
    const current = byAddress.get(r.address);
    if (current === undefined || HEAT.indexOf(cls) < HEAT.indexOf(current)) byAddress.set(r.address, cls);
  }
  return byAddress;
}

/**
 * The painted runs' classes, keyed by run then address (ADR 198: under a
 * multi-run merge each cell names the run that owns it, and a cell must be
 * judged by ITS run's comparison — another run's verdict on the same address
 * is about a different walk).
 */
export type WmsClassIndex = Map<string, Map<string, WmsClassKey>>;

export function wmsClassOf(index: WmsClassIndex, cell: Pick<OccupancyCell, "address" | "runId">): WmsClassKey | null {
  if (!cell.runId) return null;   // not_scanned: no walk, no verdict
  return index.get(cell.runId)?.get(cell.address) ?? null;
}

/** Deck classifier for `deckGroups` — null mutes the cells with no verdict. */
export function wmsDeckPaint(index: WmsClassIndex): (cell: OccupancyCell) => string | null {
  return (cell) => {
    const cls = wmsClassOf(index, cell);
    return cls ? wmsColorFor(cls) : null;
  };
}

/**
 * Carton classifier. Boxes carry no runId, so they resolve against every
 * painted run's map: a box only exists where a run counted stock, and that
 * address appears in exactly the owning run's comparison.
 */
export function wmsBoxPaint(index: WmsClassIndex): (b: BoxPlacement) => string {
  return (b) => {
    const address = boxAddress(b);
    for (const byAddress of index.values()) {
      const cls = byAddress.get(address);
      if (cls) return wmsColorFor(cls);
    }
    return WMS_NO_COMPARISON_COLOR;
  };
}

/** Legend tallies: cells per class in WMS_CLASSES order, plus the uncompared. */
export function wmsCounts(cells: readonly OccupancyCell[], index: WmsClassIndex): PaintCounts {
  const buckets = WMS_CLASSES.map(() => 0);
  let noData = 0;
  for (const cell of cells) {
    const cls = wmsClassOf(index, cell);
    if (cls === null) { noData++; continue; }
    buckets[WMS_CLASSES.findIndex((c) => c.key === cls)]++;
  }
  return { buckets, noData };
}
